import React, { useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PowerIcon from "@mui/icons-material/Power";
import {
	useTenantUtilitiesStatus,
	useUtilityControl,
} from "services/hooks/utilities";

const UTILITY_LABELS = {
	electricity: "Electricity",
	water: "Water",
};

const balanceErrorMessage = (balanceError) => {
	if (!balanceError) return null;
	if (balanceError === "enrollment_inactive") return "Utilities enrollment is inactive for this unit.";
	if (typeof balanceError === "string") return balanceError;
	if (balanceError.detail) return balanceError.detail;
	return "Could not load live utility balance.";
};

const formatQuantity = (value, unit) => {
	if (value == null || value === "") return null;
	const num = typeof value === "number" ? value : Number(value);
	if (!Number.isFinite(num)) return null;
	const rounded = Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.?0+$/, "");
	return `${rounded} ${unit}`;
};

/** Only electricity kWh and water m³ from tenant-status balance payload. */
const getDisplayBalances = (balance, { electricityEnabled, waterEnabled }) => {
	const data = balance?.data && typeof balance.data === "object" ? balance.data : balance;
	if (!data || typeof data !== "object") return [];
	const rows = [];
	if (electricityEnabled) {
		const value = formatQuantity(data.electricity_balance_kwh, "kWh");
		if (value) rows.push({ key: "electricity", label: "Electricity", value });
	}
	if (waterEnabled) {
		const value = formatQuantity(data.water_balance_m3, "m³");
		if (value) rows.push({ key: "water", label: "Water", value });
	}
	return rows;
};

const getControlUtilityRows = (utilityControl) => {
	if (!utilityControl) return [];
	if (Array.isArray(utilityControl)) return utilityControl;
	if (Array.isArray(utilityControl.utilities)) return utilityControl.utilities;
	if (Array.isArray(utilityControl.data?.utilities)) return utilityControl.data.utilities;
	return [];
};

/**
 * Interpret POST utility-control response into a short success/failure message.
 * @returns {{ ok: boolean, message: string }}
 */
const interpretControlOutcome = (result, action, utility) => {
	const label = UTILITY_LABELS[utility] || utility;
	const verb = action === "disconnect" ? "disconnect" : "reconnect";
	const past = action === "disconnect" ? "disconnected" : "reconnected";

	if (!result.success) {
		return {
			ok: false,
			message: result.message || `${label} ${verb} failed.`,
		};
	}

	const rows = getControlUtilityRows(result.data?.utility_control);
	const row = rows.find((item) => (item.utility || item.name) === utility) || rows[0];

	if (row) {
		const detail = row.detail || row.message || row.error || "";
		const resultCode = String(row.result || "").toLowerCase();
		const failed =
			resultCode === "failed" ||
			resultCode === "error" ||
			row.ok === false ||
			row.success === false ||
			Boolean(row.error);

		if (failed) {
			return {
				ok: false,
				message: detail || `${label} ${verb} failed.`,
			};
		}

		// Provider skipped because already in desired state — still surface clearly.
		if (resultCode === "skipped") {
			return {
				ok: false,
				message: detail || `${label} ${verb} was skipped.`,
			};
		}

		if (resultCode === "success" || resultCode === "ok" || !resultCode) {
			return {
				ok: true,
				message: detail || `${label} ${past} successfully.`,
			};
		}
	}

	const topStatus = String(result.data?.utility_control?.status || "").toLowerCase();
	if (topStatus === "success" || topStatus === "ok") {
		return { ok: true, message: `${label} ${past} successfully.` };
	}

	return { ok: true, message: `${label} ${past} successfully.` };
};

const readUtilityConnected = (row) => {
	if (!row || typeof row !== "object") return null;
	if (typeof row.supply_connected === "boolean") return row.supply_connected;
	if (typeof row.connected === "boolean") return row.connected;
	if (typeof row.is_connected === "boolean") return row.is_connected;
	if (typeof row.disconnected === "boolean") return !row.disconnected;
	if (typeof row.status === "string") {
		const status = row.status.toLowerCase();
		if (status.includes("disconnect")) return false;
		if (status.includes("connect")) return true;
	}
	return null;
};

const getUtilityRows = (controlState) => {
	if (!controlState) return [];
	const nested = controlState.utility_control;
	if (Array.isArray(controlState.utilities)) return controlState.utilities;
	if (Array.isArray(nested)) return nested;
	if (nested && typeof nested === "object") {
		if (Array.isArray(nested.utilities)) return nested.utilities;
		if (Array.isArray(nested.data?.utilities)) return nested.data.utilities;
	}
	return [];
};

/** @returns {boolean|null} true = disconnected, false = connected, null = unknown */
const isUtilityDisconnected = (controlState, utility = "electricity") => {
	if (!controlState) return null;
	const direct = controlState[utility];
	const directConnected = readUtilityConnected(direct);
	if (directConnected != null) return !directConnected;

	const row = getUtilityRows(controlState).find(
		(item) => (item.utility || item.name) === utility
	);
	const connected = readUtilityConnected(row);
	if (connected != null) return !connected;
	return null;
};

const TenantUtilitiesStatus = ({ tenantId, occupancyId }) => {
	const { data, loading, error, refetch } = useTenantUtilitiesStatus(tenantId, occupancyId);
	const enrolled = Boolean(data?.enrolled);
	const {
		controlState,
		loading: controlLoading,
		submitting,
		error: controlError,
		disconnect,
		reconnect,
		refetch: refetchControl,
	} = useUtilityControl(tenantId, occupancyId, { enabled: enrolled });

	const [confirmAction, setConfirmAction] = useState(null);
	const [resultDialog, setResultDialog] = useState(null);

	const utilities = data?.utilities;
	const electricityEnabled = utilities?.electricity_enabled !== false;
	const waterEnabled = utilities?.water_enabled !== false;
	const balanceErr = balanceErrorMessage(data?.balance_error);
	const balanceRows = useMemo(
		() =>
			getDisplayBalances(data?.balance, {
				electricityEnabled,
				waterEnabled,
			}),
		[data?.balance, electricityEnabled, waterEnabled]
	);

	const electricityDisconnected = useMemo(
		() => isUtilityDisconnected(controlState, "electricity"),
		[controlState]
	);
	const waterDisconnected = useMemo(
		() => isUtilityDisconnected(controlState, "water"),
		[controlState]
	);

	const controllableUtilities = useMemo(() => {
		const rows = [];
		if (electricityEnabled) {
			rows.push({
				key: "electricity",
				label: UTILITY_LABELS.electricity,
				icon: <BoltIcon />,
				disconnected: electricityDisconnected,
			});
		}
		if (waterEnabled) {
			rows.push({
				key: "water",
				label: UTILITY_LABELS.water,
				icon: <WaterDropIcon />,
				disconnected: waterDisconnected,
			});
		}
		return rows;
	}, [electricityEnabled, waterEnabled, electricityDisconnected, waterDisconnected]);

	const handleConfirm = async () => {
		if (!confirmAction?.action || !confirmAction?.utility) return;
		const { action, utility } = confirmAction;
		const result =
			action === "disconnect" ? await disconnect([utility]) : await reconnect([utility]);
		const outcome = interpretControlOutcome(result, action, utility);
		setConfirmAction(null);
		setResultDialog({
			ok: outcome.ok,
			title: outcome.ok
				? action === "disconnect"
					? "Disconnect successful"
					: "Reconnect successful"
				: action === "disconnect"
				? "Disconnect failed"
				: "Reconnect failed",
			message: outcome.message,
		});
		refetch();
		refetchControl();
	};

	if (!tenantId || !occupancyId) return null;

	if (loading) {
		return (
			<Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
				<CircularProgress size={18} />
				<Typography variant="body2" color="text.secondary">
					Loading utilities…
				</Typography>
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="warning" sx={{ maxWidth: 420 }}>
				{error}
			</Alert>
		);
	}

	if (!data) return null;

	if (!enrolled) {
		const inactive = utilities && utilities.is_active === false;
		return (
			<Box sx={{ maxWidth: 420, textAlign: "right" }}>
				<Chip
					size="small"
					label={inactive ? "Utilities inactive" : "Utilities not enabled"}
					color="default"
					variant="outlined"
					sx={{ mb: 0.5 }}
				/>
				<Typography variant="body2" color="text.secondary">
					{inactive
						? "This unit has a utilities enrollment that is currently inactive."
						: "Utilities not enabled for this unit."}
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ maxWidth: 460, textAlign: "right" }}>
			<Stack
				direction="row"
				spacing={1}
				justifyContent="flex-end"
				alignItems="center"
				flexWrap="wrap"
				sx={{ mb: 0.5 }}
			>
				<Chip size="small" label="Utilities enrolled" color="success" variant="outlined" />
				{utilities?.provider_account_number && (
					<Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
						Acct {utilities.provider_account_number}
					</Typography>
				)}
			</Stack>

			<Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" sx={{ mb: 0.5 }}>
				{controllableUtilities.map((utility) => (
					<Chip
						key={utility.key}
						size="small"
						icon={utility.icon}
						label={
							controlLoading
								? `${utility.label}…`
								: utility.disconnected === true
								? `${utility.label} disconnected`
								: utility.disconnected === false
								? `${utility.label} connected`
								: utility.label
						}
						color={
							utility.disconnected === true
								? "warning"
								: utility.disconnected === false
								? "success"
								: "default"
						}
						variant="outlined"
					/>
				))}
			</Stack>

			{!balanceErr && balanceRows.length > 0 && (
				<Stack spacing={0.25} alignItems="flex-end" sx={{ mb: 1 }}>
					{balanceRows.map((row) => (
						<Typography key={row.key} variant="body2" color="text.secondary">
							{row.label}:{" "}
							<Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
								{row.value}
							</Box>
						</Typography>
					))}
				</Stack>
			)}
			{balanceErr && (
				<Alert severity="warning" sx={{ mb: 1, textAlign: "left" }}>
					{balanceErr}
				</Alert>
			)}

			{controllableUtilities.length > 0 && (
				<Stack spacing={0.75} alignItems="flex-end" sx={{ mb: 1 }}>
					{controlLoading && <CircularProgress size={18} />}
					{!controlLoading &&
						controllableUtilities.map((utility) => (
							<Stack
								key={utility.key}
								direction="row"
								spacing={1}
								justifyContent="flex-end"
								alignItems="center"
								flexWrap="wrap"
							>
								{utility.disconnected === true && (
									<Button
										size="small"
										variant="contained"
										color="success"
										startIcon={<PowerIcon />}
										disabled={submitting}
										onClick={() =>
											setConfirmAction({ action: "reconnect", utility: utility.key })
										}
									>
										Reconnect {utility.label}
									</Button>
								)}
								{utility.disconnected === false && (
									<Button
										size="small"
										variant="outlined"
										color="error"
										startIcon={<PowerSettingsNewIcon />}
										disabled={submitting}
										onClick={() =>
											setConfirmAction({ action: "disconnect", utility: utility.key })
										}
									>
										Disconnect {utility.label}
									</Button>
								)}
								{utility.disconnected == null && (
									<Typography variant="caption" color="text.secondary">
										{utility.label}: status unavailable
									</Typography>
								)}
							</Stack>
						))}
				</Stack>
			)}

			{controlError && (
				<Alert severity="error" sx={{ mt: 0.5, textAlign: "left" }}>
					{controlError}
				</Alert>
			)}

			<Dialog open={Boolean(confirmAction)} onClose={() => !submitting && setConfirmAction(null)}>
				<DialogTitle>
					{confirmAction?.action === "disconnect"
						? `Disconnect ${UTILITY_LABELS[confirmAction?.utility] || "utility"}?`
						: `Reconnect ${UTILITY_LABELS[confirmAction?.utility] || "utility"}?`}
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2">
						{confirmAction?.action === "disconnect"
							? `This will disconnect ${String(
									UTILITY_LABELS[confirmAction?.utility] || "utility"
							  ).toLowerCase()} for rent default on this occupancy.`
							: `This will reconnect ${String(
									UTILITY_LABELS[confirmAction?.utility] || "utility"
							  ).toLowerCase()} after rent has been paid.`}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmAction(null)} disabled={submitting}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color={confirmAction?.action === "disconnect" ? "error" : "success"}
						onClick={handleConfirm}
						disabled={submitting}
					>
						{submitting
							? "Working…"
							: confirmAction?.action === "disconnect"
							? "Disconnect"
							: "Reconnect"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={Boolean(resultDialog)} onClose={() => setResultDialog(null)}>
				<DialogTitle>{resultDialog?.title}</DialogTitle>
				<DialogContent>
					<Alert severity={resultDialog?.ok ? "success" : "error"} sx={{ mt: 0.5 }}>
						{resultDialog?.message}
					</Alert>
				</DialogContent>
				<DialogActions>
					<Button variant="contained" onClick={() => setResultDialog(null)}>
						OK
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default TenantUtilitiesStatus;
